/**
 * useTicketComposerAttachments - Staging de anexos do composer de tickets
 *
 * Adaptado do padrão `useFileAttachment` do chat
 * (`@/modules/chat/hooks/useFileAttachment.ts`) para o composer de tickets:
 * cada arquivo adicionado sobe IMEDIATAMENTE via `ticketsService.uploadTicketFile`
 * (`POST /uploads?folder=tickets`) e passa pelos estados
 * `pending -> uploading -> completed | failed`.
 *
 * Só anexos `completed` entram no payload de envio (`getCompletedAttachments`,
 * shape `ActivityAttachment` — o mesmo aceito por `POST /tickets/:id/reply` e
 * `POST /tickets/:id/activities`). Falhas ficam visíveis no chip com retry
 * (`retryUpload`) sem bloquear o envio do texto — o caller decide se quer
 * aguardar (`isUploading`) antes de submeter.
 */

import { useCallback, useState } from 'react';
import type { ActivityAttachment } from '@troia-v3/types';
import { ticketsService } from '../services/ticketsService';

export type ComposerAttachmentStatus = 'pending' | 'uploading' | 'completed' | 'failed';

export interface ComposerAttachment {
  id: string;
  file: File;
  /** Data URI de preview — só populado para imagens */
  preview?: string;
  uploadStatus: ComposerAttachmentStatus;
  uploadProgress: number;
  error?: string;
  /** Resultado do upload (URL final + metadata retornada pelo backend) — só presente quando `completed` */
  result?: ActivityAttachment;
}

interface UseTicketComposerAttachmentsOptions {
  /** Número máximo de anexos permitidos por mensagem. Default: 10 (mesmo limite do backend). */
  maxFiles?: number;
  /** Chamado quando `addFiles` recebe mais arquivos do que o slot disponível (o excedente é descartado). */
  onLimitExceeded?: (maxFiles: number) => void;
}

const createAttachmentId = (): string =>
  `att-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const createImagePreview = (file: File): Promise<string | undefined> => {
  if (!file.type.startsWith('image/')) return Promise.resolve(undefined);
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => resolve(undefined);
    reader.readAsDataURL(file);
  });
};

export function useTicketComposerAttachments(options: UseTicketComposerAttachmentsOptions = {}) {
  const { maxFiles = 10, onLimitExceeded } = options;
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([]);

  const updateAttachment = useCallback(
    (id: string, updates: Partial<ComposerAttachment>) => {
      setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, ...updates } : a)));
    },
    []
  );

  const uploadAttachment = useCallback(
    async (id: string, file: File) => {
      updateAttachment(id, { uploadStatus: 'uploading', uploadProgress: 0, error: undefined });
      try {
        const result = await ticketsService.uploadTicketFile(file, (progress) => {
          updateAttachment(id, { uploadProgress: progress });
        });
        updateAttachment(id, {
          uploadStatus: 'completed',
          uploadProgress: 100,
          result: {
            url: result.url,
            filename: result.filename,
            ...(result.size !== undefined && { size: result.size }),
            ...(result.mimeType !== undefined && { mimeType: result.mimeType }),
          },
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Falha no upload do anexo';
        updateAttachment(id, { uploadStatus: 'failed', error: message });
      }
    },
    [updateAttachment]
  );

  /**
   * Adiciona arquivos à staging e dispara upload imediatamente pra cada um.
   * Respeita `maxFiles` — excedente é descartado com aviso via `onLimitExceeded`.
   */
  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const incoming = Array.from(files);
      if (incoming.length === 0) return;

      const availableSlots = maxFiles - attachments.length;
      if (availableSlots <= 0) {
        onLimitExceeded?.(maxFiles);
        return;
      }

      const accepted = incoming.slice(0, availableSlots);
      if (incoming.length > accepted.length) {
        onLimitExceeded?.(maxFiles);
      }

      const newAttachments: ComposerAttachment[] = accepted.map((file) => ({
        id: createAttachmentId(),
        file,
        uploadStatus: 'pending',
        uploadProgress: 0,
      }));

      setAttachments((prev) => [...prev, ...newAttachments]);

      // Upload + preview disparados FORA do updater de estado (efeitos
      // colaterais não pertencem ao reducer) — cada anexo evolui de forma
      // independente via `updateAttachment`.
      newAttachments.forEach((attachment) => {
        void uploadAttachment(attachment.id, attachment.file);
        void createImagePreview(attachment.file).then((preview) => {
          if (preview) updateAttachment(attachment.id, { preview });
        });
      });
    },
    [attachments.length, maxFiles, onLimitExceeded, uploadAttachment, updateAttachment]
  );

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const retryUpload = useCallback(
    (id: string) => {
      const attachment = attachments.find((a) => a.id === id);
      if (!attachment) return;
      void uploadAttachment(id, attachment.file);
    },
    [attachments, uploadAttachment]
  );

  const clearAttachments = useCallback(() => {
    setAttachments([]);
  }, []);

  /** Anexos com upload concluído, já no shape aceito pelo backend (`ActivityAttachment`). */
  const getCompletedAttachments = useCallback((): ActivityAttachment[] => {
    const completed: ActivityAttachment[] = [];
    for (const attachment of attachments) {
      if (attachment.uploadStatus === 'completed' && attachment.result) {
        completed.push(attachment.result);
      }
    }
    return completed;
  }, [attachments]);

  const isUploading = attachments.some(
    (a) => a.uploadStatus === 'pending' || a.uploadStatus === 'uploading'
  );
  const hasFailedUploads = attachments.some((a) => a.uploadStatus === 'failed');

  return {
    attachments,
    addFiles,
    removeAttachment,
    retryUpload,
    clearAttachments,
    getCompletedAttachments,
    isUploading,
    hasFailedUploads,
  };
}
